import {React, useState, useEffect, useRef, Fragment} from 'react';

import styles from './scorecard.module.css'
import constants from '../../../constants';
import utils from '../../../utils/utils.js'
import api from '../../../routes/api.js'

import { useApiProvider } from '../../../providers/ApiProvider.js';
import { useLeagueTypeProvider } from '../../../providers/LeagueTypeProvider.js';

import ConfirmModal from '../../common/ConfirmModal/ConfirmModal.js';

import Select from '../../common/Select/Select';
import PrintableScorecard from './PrintableScorecard.js';

const InfoBox =({team, matchupResults}) => {

  const isLineupComplete = (team) => {
    const lineup = matchupResults[constants.lineups][constants.lineups][constants.values];

    const playerField1 = `${team}PlayerId1`;
    const playerField2 = `${team}PlayerId2`;

    return lineup.every(
      (game) => game[playerField1] != null && game[playerField2] != null
    );
  };

  const teamName = team === constants.home ? matchupResults[constants.matchup][constants.homeTeam][constants.teamName] : matchupResults[constants.matchup][constants.awayTeam][constants.teamName]; 
  const teamId = team === constants.home ? matchupResults[constants.matchup][constants.homeTeam][constants.teamId] : matchupResults[constants.matchup][constants.awayTeam][constants.teamId] 
  // Darker color (text, border, icon)
  let primaryColor = "";
  // Lighter color (background)
  let secondaryColor = "";

  let message = "";
  let src = "";

  if (!isLineupComplete(team)){
    primaryColor = constants.colors.cplRed;
    secondaryColor = constants.colors.cplPink;
    message = constants.header.lineupNotCompleted;
    src = "error-red.svg"; 
  }
  else if (matchupResults[constants.api.penalties][constants.values].length > 0) {
    const penalties = matchupResults[constants.api.penalties][constants.values];
    let teamPenalty = false;
    
    for (let p in penalties) {
      if (penalties[p][constants.teamId] == teamId && penalties[p][constants.api.penaltyTypeId] === constants.penalties.late) {
        teamPenalty = true;
        break;
      }
    }

    if (teamPenalty) {
      primaryColor = constants.colors.cplOrange
      secondaryColor = constants.colors.cplLightOrange;
      message = constants.header.lineupSavedLate;
      src = "warning-yellow.svg"
    }
    else {
      primaryColor = constants.colors.cplDarkGreen;
      secondaryColor = constants.colors.cplLightGreen;
      message = constants.header.lineupSaved;
      src = "check-green.svg";
    }
  }
  else {
    primaryColor = constants.colors.cplDarkGreen;
    secondaryColor = constants.colors.cplLightGreen;
    message = constants.header.lineupSaved;
    src = "check-green.svg";
  }
  
  return (
    <div className={styles.SaveIndicatorBox} style={{border: `1px solid ${primaryColor}`, background: secondaryColor, color: primaryColor}}>
      <img className={styles.SaveIndicatorIcon} src={`/icons/${src}`}/>
      <span className={styles.SaveIndicatorText}>{teamName + " " + message}</span>
      <span className={styles.SaveIndicatorSpacer} aria-hidden="true" />
    </div>
  )
}


const Scorecard = ({loggedInUser, matchupResults,
                    games, setGames,
                    dirtyGames, updateDirtyGames, 
                    undo, canUndo, clearHistory,
                    homeTeam, awayTeam,
                    players, genderPlayers, updateMatch,
                    isSwapping, setIsSwapping, isLineupLocked
                 }) => {
  const { setDialog, isLoading, setIsLoading, LoadingSpinner} = useApiProvider();
  const { leagueType } = useLeagueTypeProvider();

  // selectedPlayers is a filtered version of all players that have been selected on the Rosters page
  const [selectedPlayers, setSelectedPlayers] = useState({
    homeMales: [],
    homeFemales: [],
    awayMales: [],
    awayFemales: [],
  });

  // selectedGenderPlayers is a filtered version of all gender-league players that have been selected on the Rosters page
  const [selectedGenderPlayers, setSelectedGenderPlayers] = useState({
    homePlayers: [],
    awayPlayers: [],
  });

  const [isPatching, setIsPatching] = useState(false);
  const [roundScroll, setRoundScroll] = useState("");

  // const intervalRef = useRef(null);
  // const intervalStartedRef = useRef(false);

  const confirmDialogRef = useRef(null);

  const openConfirmDialog = () => {
    if (confirmDialogRef.current){
      confirmDialogRef.current.style.display = 'flex';
      confirmDialogRef.current.showModal();
    }
  }

  const closeConfirmDialog = () => {
    if (confirmDialogRef.current){
      confirmDialogRef.current.style.display = 'none';
      confirmDialogRef.current.close();
    }
  }

  const roundSelectOptions = [];
  for (let i = 0; i < 8; i++){
    roundSelectOptions.push({key: "round-" + (i+1), value: "round-" + (i+1), label: "Round "+ (i+1), disabled: false})
  }

  // I know. Don't judge me.
  const getOptions = (game, row, team) => {
    const options = [{key: '', value: '', label: '', disabled: false}];

    // Handle mixed logic separately for code readability]
    if (game[constants.matchType] == constants.mixed) {
      if (team == constants.awayPlayer1) {
        selectedPlayers.awayFemales.forEach((p) => {
          const name = p[constants.firstName] + " " + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
      else if (team == constants.awayPlayer2){
        selectedPlayers.awayMales.forEach((p) => {
          const name = p[constants.firstName] + " " + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
      else if (team == constants.homePlayer1) {
        selectedPlayers.homeFemales.forEach((p) => {
          const name = p[constants.firstName] + " " + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
      else if (team == constants.homePlayer2){
        selectedPlayers.homeMales.forEach((p) => {
          const name = p[constants.firstName] + " " + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
    }  

    // Notice lowercase male check
    else if (game[constants.matchType] == constants.male){
      if (team == constants.homePlayer1 || team == constants.homePlayer2) {
        selectedPlayers.homeMales.forEach((p) => {
          const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
      else if (team == constants.awayPlayer1 || team == constants.awayPlayer2) {
        selectedPlayers.awayMales.forEach((p) => {
          const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
    } else {
      if (team == constants.homePlayer1 || team == constants.homePlayer2) {
        selectedPlayers.homeFemales.forEach((p) => {
          const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
      else if (team == constants.awayPlayer1 || team == constants.awayPlayer2) {
        selectedPlayers.awayFemales.forEach((p) => {
          const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
          options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
        })
      }
    }

    return options;
  }

  const getGenderOptions = (game, row, team) => {
    const options = [{key: '', value: '', label: '', disabled: false}];

    if (team == constants.homePlayer1 || team == constants.homePlayer2) {
      selectedGenderPlayers.homePlayers.forEach((p) => {
        const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
        options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
      })
    }
    else if (team == constants.awayPlayer1 || team == constants.awayPlayer2) {
      selectedGenderPlayers.awayPlayers.forEach((p) => {
        const name = p[constants.firstName] + " " + (p[constants.middleName] ? p[constants.middleName][0] + ". " : "") + p[constants.lastName];
        options.push({key: p[constants.playerId], value: p[constants.playerId], label: leagueType === constants.pages.localLeagues ? `${p[constants.ranking]}. ${name}` : name, disabled: togglePlayerDisabled(row, p)});
      })
    }

    return options;
  }

  /* Takes in an object where each attribute is an array of players. Returns the full name of the found player*/
  const getPlayerName = (playerId) => {
    const players = leagueType === constants.pages.genderLeagues ? selectedGenderPlayers : selectedPlayers;
    for (let key in players) {
      const foundPlayer = players[key].find((p) => p[constants.playerId] == playerId);
      if (foundPlayer) return foundPlayer[constants.firstName] + " " + foundPlayer[constants.lastName];
    }
  }

  const togglePlayerDisabled = (round, player) => {
    // For local league admins, this should always return true
    if (leagueType === constants.pages.localLeagues && loggedInUser === constants.both) return false;

    const currentPlayerId = player[constants.playerId];
    let foundPlayer = round.findIndex(obj => Object.values(obj).some(id => id === currentPlayerId));
    
    return foundPlayer > -1
  }

  const onPlayerChange = (e, currentGame, player) => {
    const newGames = dirtyGames.map((g) =>
      g[constants.gameNumber] == currentGame[constants.gameNumber]
        ? { ...g, [player]: e.currentTarget.value || null }
        : g
    );
    updateDirtyGames(newGames);
  }

  const onScoreChange = (e, game, team) => {
    const score = e.currentTarget.value.trim();
    const isScoreValid = score.match(constants.regex.numberRegex);

    if (isScoreValid){
      const newGames = dirtyGames.map((g) =>
        g[constants.gameNumber] == game[constants.gameNumber]
          ? { ...g, [team]: score == '' ? null : parseInt(score) }
          : g
      );
      updateDirtyGames(newGames);
    }
  }

  const scrollToRound = (e) => {
    setRoundScroll(e.currentTarget.value);
  }

  const toggleSaveButton = () => {
    // Only allow modifying after the match if the user is an admin
    if (matchupResults[constants.matchup][constants.endResult] != null){
      if (loggedInUser === constants.both) return false;
      if (isLineupLocked && isSwapping){
        let disabled = true;
        // If we're swapping, only enable save if there are missing players
        for (let i=0; i < rounds.flat().length; i++){
          if (dirtyGames[i][constants.homePlayer1] == null 
            || dirtyGames[i][constants.homePlayer2] == null
            || dirtyGames[i][constants.awayPlayer1] == null 
            || dirtyGames[i][constants.awayPlayer2] == null) {
              disabled = false;
              break;
          }
        }
        return disabled;
      }
    } 

    // Don't let the user change while saving/submitting
    if (isPatching) return true;

    // Can't save if not logged in
    if (loggedInUser == "") return true;

    return false;
  }

  /* We only allow the official "submit" once all the players and scores are entered.*/
  const toggleSubmitButton = () => {
    let disabled = false;

    // Don't let them submit if not logged in
    if (loggedInUser == "") return true;

    // Don't let the user change while saving/submitting
    if (isPatching) return true;

    if (loggedInUser == "both") return false;
    
    // Only allow modifying after the match if the user is an admin
    if (matchupResults[constants.matchup][constants.endResult] != null) return loggedInUser != "both";

    // If the match isn't over, but there are more than 32 games (game is in overtime) let the submit happen
    // Don't return the comparitor because we need to continue the logic flow
    if (matchupResults[constants.lineups][constants.lineups][constants.values].length > 32) {
      return true;
    };

    for (let i=0; i < rounds.flat().length; i++){
      if (dirtyGames[i][constants.homePlayer1] == null 
        || dirtyGames[i][constants.homePlayer2] == null
        || dirtyGames[i][constants.homeScore] == null
        || dirtyGames[i][constants.awayPlayer1] == null 
        || dirtyGames[i][constants.awayPlayer2] == null 
        || dirtyGames[i][constants.awayScore] == null) {
          disabled = true;
          break;
      }
    }

    return disabled;
  }

  // Admin can always clear
  // Disable if logged out, or match is submitted already
  const toggleClearButton = () => {
    
    if (loggedInUser == "both") {
      return false;
    } 
    else {
      if (loggedInUser == "" || matchupResults[constants.matchup][constants.endResult] != null) return true;
    }
  }

  // Determines if the displayed player info is text or the select inputs
  // If team is null, then we're toggling the score input
  const toggleInput = (team) => {
    // For local leagues we only let admins change because lineups are automatically set
    if (leagueType === constants.pages.localLeagues) {
      // If it's the score, we're setting disabled vs checking if the return is true so return the opposite
      if (team == null){
        return loggedInUser === ""; 
      }
      else {
        return loggedInUser === constants.both; 
      }
    }

    // We expect two different results depending on player select/score input.
    // This method is used to toggle JSX elements and disabled, return two different
    // things depending on which it is
    if (matchupResults[constants.matchup][constants.endResult] != null) {
      // Score input
      if (team == null || team == undefined) {
        return loggedInUser != constants.both
      }
      // Player input
      else {
        return loggedInUser == constants.both
      }
    } 

    // Don't let the user change while saving/submitting
    if (isPatching) return true;

    if (team == constants.away) {
      return (loggedInUser == constants.away || loggedInUser == constants.both)
    } else if (team == constants.home) {
      return (loggedInUser == constants.home || loggedInUser == constants.both)
    }

    // Allow score updates unless not logged in
    if (loggedInUser == "") return true;

    return false;
  }

  const togglePlayerSelectDisabled = (player) => {
    if (isPatching) return true;
    // If the lineup is locked, only enable if the player is empty
    if (isLineupLocked) return player != null;
  }

  const isLineupEmpty = () => {
    return !dirtyGames.some(g => {
      if (loggedInUser == constants.home || loggedInUser == constants.both) {
        if (g[constants.homePlayer1] || g[constants.homePlayer2] || g[constants.homeScore]) return true;
      }
      if (loggedInUser == constants.away || loggedInUser == constants.both) {
        if (g[constants.awayPlayer1] || g[constants.awayPlayer2] || g[constants.awayScore]) return true;
      }
      return false;
    });
  };

  const clearLineup = async () => {
    if (loggedInUser != "") {

      const newGames = dirtyGames.map(g => {
        const newGame = { ...g };
        if (loggedInUser == constants.home || loggedInUser == constants.both) {
          newGame[constants.homePlayer1] = null;
          newGame[constants.homePlayer2] = null;
          newGame[constants.homeScore] = null;
        }
        if (loggedInUser == constants.away || loggedInUser == constants.both) {
          newGame[constants.awayPlayer1] = null;
          newGame[constants.awayPlayer2] = null;
          newGame[constants.awayScore] = null;
        }
        return newGame;
      });

      updateDirtyGames(newGames);
      closeConfirmDialog();
    }
  }

  const getChangedFields = () => {
    const changedFields = games.reduce((acc, item, index) => {
      const other = dirtyGames[index];
      if (!other) return acc;

      const fields = {};
      Object.keys(item).forEach((key) => {
        if (item[key] !== other[key]) fields[key] = (typeof other[key] == 'number' ? other[key].toString() : other[key]);
      });

      if (Object.keys(fields).length > 0) acc.push({ gameNumber: item.gameNumber, fields });
      return acc;
    }, []);

    return changedFields;
  }

  const patchLineup = async (isSubmit) => {
    setIsPatching(true);
    const request = {};
    request[constants.loginCode] = loggedInUser;
    request[constants.lineups] = getChangedFields();
    const url = isSubmit ? constants.uri.matchup(dirtyGames[0][constants.matchupId]) + "?submit=true" : constants.uri.matchup(dirtyGames[0][constants.matchupId]);
    try {
      const baseUrl = utils.getBaseUrl(leagueType);

      const submitResponse = await api.patch(
        baseUrl,
        url,
        JSON.stringify(request),
      );

      if (submitResponse.hasOwnProperty('errors') && submitResponse.errors != null) {
        setDialog({
          title: submitResponse.errors.split(".")[0],
          code: "N/A",
          message: {message:submitResponse.errors.split(".")[1]},
          isError: true
        });
      }
      else if (submitResponse.errors != null) throw submitResponse;

      // Now that the save completed, set the "default" data so it matches the backened
      setGames(dirtyGames);
      updateMatch(false);
      clearHistory();
      setIsPatching(false);
      setIsSwapping(false);

    }
    catch (err) {
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage, 
        code: err.status ? err.status : "N/A", 
        message: err.message, 
        isError: true});
      setIsPatching(false);
    }
  }

  // Restructure games array to better fit the rendering procedure
  const rounds = [];
  for (let i = 0; i < dirtyGames.length; i += 4) {
    rounds.push(dirtyGames.slice(i, i + 4));
  }

  const getPlayerState = () => {
    return leagueType === constants.pages.genderLeagues ? selectedGenderPlayers : selectedPlayers;
  }

  useEffect (() => {
    if (leagueType === constants.pages.genderLeagues) {
      const newSelectedGenderPlayers = {
        homePlayers: [],
        awayPlayers: []
      }
      genderPlayers.homePlayers.forEach((p) => {if (p.selected) newSelectedGenderPlayers.homePlayers.push(p)});
      genderPlayers.awayPlayers.forEach((p) => {if (p.selected) newSelectedGenderPlayers.awayPlayers.push(p)});

      setSelectedGenderPlayers(newSelectedGenderPlayers);
    }
    else {
      const newSelectedPlayers = {
        homeMales: [],
        homeFemales: [],
        awayMales: [],
        awayFemales: []
      }
      players.homeMales.forEach((p) => {if (p.selected) newSelectedPlayers.homeMales.push(p)});
      players.homeFemales.forEach((p) => {if (p.selected) newSelectedPlayers.homeFemales.push(p)});
      players.awayMales.forEach((p) => {if (p.selected) newSelectedPlayers.awayMales.push(p)});
      players.awayFemales.forEach((p) => {if (p.selected) newSelectedPlayers.awayFemales.push(p)});

      setSelectedPlayers(newSelectedPlayers);
    }


    // Save every 5 minutes as long as the user is logged in
    // if (loggedInUser != "" && !intervalStartedRef.current) {
    //   intervalRef.current = setInterval(() => {
    //     saveLineup();
    //   }, 300000);
    //   intervalStartedRef.current = true;
    // }

    if (roundScroll != "") {
      const round = document.getElementById(roundScroll);
      round.scrollIntoView({ behavior: 'smooth' });
    }

  }, [roundScroll]);

  return (
    <>
      <PrintableScorecard
        rounds={rounds}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        getPlayerName={getPlayerName}
      />
      <div className={styles.Scorecard}>
        <ConfirmModal 
          ref={confirmDialogRef}
          onSubmit={clearLineup}
          message={"This will clear your ENTIRE lineup!"}
          onCancel={closeConfirmDialog}
          onConfirm={clearLineup}
          />

        <div className={styles.ButtonGroup}>
          <div className={styles.ScoreContainer}>
            <button className={styles.ButtonLeft} onClick={window.print}>Print</button>
            <Select onChange={(e) => {scrollToRound(e)}} additionalStyle={styles.RoundSelect} currentValue={roundScroll} options={roundSelectOptions}/>
          </div>
          <div className={styles.ScoreContainer}>
            <button className={styles.ButtonRight} disabled={!canUndo} onClick={undo}>{isPatching ? <div ><LoadingSpinner size={24}/></div> : constants.labels.undo }</button>
            <button className={styles.ButtonRight} disabled={toggleClearButton()} onClick={() => { if (!isLineupEmpty()) openConfirmDialog(); }}>{isPatching ? <div ><LoadingSpinner size={24}/></div> : constants.labels.clear }</button>
            <button className={styles.ButtonRight} disabled={toggleSaveButton()} onClick={() => {patchLineup(false)}}>{isPatching ? <div ><LoadingSpinner size={24}/></div> : constants.labels.save }</button>
            <button className={styles.ButtonRight} style={{width: 'auto'}} disabled={toggleSubmitButton()} onClick={() => {patchLineup(true)}}>{isPatching ? <div ><LoadingSpinner size={24}/></div> : constants.labels.submit }</button>
          </div>
        </div>

      <div className={styles.GamesContainer}>
        <div className={styles.SaveIndicatorGroup}>
          <InfoBox team={constants.away} matchupResults={matchupResults}/>
          <InfoBox team={constants.home} matchupResults={matchupResults}/>
        </div>
        {rounds.map((round, i) => {
          return(
            <div key={`${rounds[i][0][constants.matchupId]}-${rounds[i][0][constants.id]}`}className={styles.RoundContainer}>
              <div id={`round-${i+1}`} className={styles.RoundTitle}>{i >= 8 ? "Overtime!" : "Round " + (i+1) + " " + (round[0][constants.matchType] == constants.mixed ? "Mixed" : "Gendered") + " Doubles"}</div>
              <div className={styles.MatchupContainer}>

                  {round.map((game, j) => {
                    return (
                      <Fragment key={`${game[constants.matchupId]}-${game[constants.gameNumber]}`}>
                        <div id={game[constants.gameNumber]} className={styles.MatchContainer}>
                          <div className={styles.Team}>
                            <div className={styles.TeamName}>{awayTeam.name}</div>
                              <div className={styles.TeamColumn}>
                                {toggleInput(constants.away) ? 
                                <Fragment>
                                  <Select
                                    disabled={togglePlayerSelectDisabled(game[constants.awayPlayer1])}
                                    onChange={(e) => onPlayerChange(e, game, constants.awayPlayer1)}
                                    additionalStyle={styles.PlayerSelect}
                                    currentValue={game[constants.awayPlayer1] ? game[constants.awayPlayer1] : ''}
                                    options={leagueType === constants.pages.genderLeagues ? getGenderOptions(game, round, constants.awayPlayer1) : getOptions(game, round, constants.awayPlayer1)}/>

                                  <div className={styles.TeamName}>&</div>

                                  <Select
                                    disabled={togglePlayerSelectDisabled(game[constants.awayPlayer2])}
                                    onChange={(e) => onPlayerChange(e, game, constants.awayPlayer2)}
                                    additionalStyle={styles.PlayerSelect}
                                    currentValue={game[constants.awayPlayer2] ? game[constants.awayPlayer2] : ''}
                                    options={leagueType === constants.pages.genderLeagues ? getGenderOptions(game, round, constants.awayPlayer2) : getOptions(game, round, constants.awayPlayer2)}/>
                                </Fragment>

                                : <Fragment>
                                    <div className={styles.TeamName}>{game[constants.awayPlayer1] ? getPlayerName(game[constants.awayPlayer1]) : "Player 1"}</div>
                                    <div className={styles.TeamName}>&</div>
                                    <div className={styles.TeamName}>{game[constants.awayPlayer2] ? getPlayerName(game[constants.awayPlayer2]) : "Player 2"}</div>
                                  </Fragment>}
                              </div>
                            
                            <div className={styles.ScoreContainer}>
                              <input 
                                disabled={toggleInput()}
                                className={styles.ScoreInput}
                                type="text"
                                value={game[constants.awayScore] ?? ''}
                                onChange={(e) => onScoreChange(e, game, constants.awayScore)}/>
                            </div>
                          </div>

                          {/* Hacky, but add empty divs so this all lines up vertically*/}
                          <div className={styles.VsColumn}>
                            <div className={styles.TopSpacer}></div>
                            <div className={styles.VS}>VS</div>
                            <div className={styles.BottomSpacer}></div>
                            <div className={styles.Dash}>—</div>
                          </div>

                          <div className={styles.Team}>
                            <div className={styles.TeamName}>{homeTeam.name}</div>
                            <div className={styles.TeamColumn}>
                            {toggleInput(constants.home) ? 
                            <Fragment>
                              <Select
                                disabled={togglePlayerSelectDisabled(game[constants.homePlayer1])}
                                onChange={(e) => onPlayerChange(e, game, constants.homePlayer1)} 
                                additionalStyle={styles.PlayerSelect} 
                                currentValue={game[constants.homePlayer1] ? game[constants.homePlayer1] : ''}
                                options={leagueType === constants.pages.genderLeagues ? getGenderOptions(game, round, constants.homePlayer1) : getOptions(game, round, constants.homePlayer1)}/>

                              <div className={styles.TeamName}>&</div>

                              <Select
                                disabled={togglePlayerSelectDisabled(game[constants.homePlayer2])}
                                onChange={(e) => onPlayerChange(e, game, constants.homePlayer2)} 
                                additionalStyle={styles.PlayerSelect} 
                                currentValue={game[constants.homePlayer2] ? game[constants.homePlayer2] : ''}
                                options={leagueType === constants.pages.genderLeagues ? getGenderOptions(game, round, constants.homePlayer2) : getOptions(game, round, constants.homePlayer2)}/>
                            </Fragment>

                            : <Fragment>
                                <div className={styles.TeamName}>{game[constants.homePlayer1] ? getPlayerName(game[constants.homePlayer1]) : "Player 1"}</div>
                                <div className={styles.TeamName}>&</div>
                                <div className={styles.TeamName}>{game[constants.homePlayer2] ? getPlayerName(game[constants.homePlayer2]) : "Player 2"}</div>
                              </Fragment>}
                            </div>

                            <div className={styles.ScoreContainer}>
                              <input 
                                disabled={toggleInput()}
                                className={styles.ScoreInput}
                                type="text"
                                value={game[constants.homeScore] ?? ''}
                                onChange={(e) => onScoreChange(e, game, constants.homeScore)}/>
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

      </div>
    </>
  )
}

export default Scorecard;